import { RepositoryTimeline } from '@types'

import { stringToDateTime, stringToLocalDateString } from '@/helpers/dates'

import { PERIOD_TYPE_OPTIONS } from './const'

type PeriodFreq = RepositoryTimeline['freq']

export type AxisPeriod = {
  date: string
  /** The axis tick's own label — month/quarter/year/day, shown only in the desktop (non-dense) axis. */
  label: string
  yearFull: string
  /** 2-digit year, prefixed with an apostrophe — used by the dense (mobile) axis, which has no room for the full year. */
  yearShort: string
  /** True for the first period, or the first period of a new year — where the axis draws a year marker. */
  isYearStart: boolean
}

export const periodTypeLabel = (freq: PeriodFreq) =>
  PERIOD_TYPE_OPTIONS.find(option => option.value === freq)?.label ?? freq?.toUpperCase() ?? ''

const axisLabel = (date: string, freq: PeriodFreq, withYear = false) => {
  const dt = stringToDateTime(date)
  if (!dt) return date
  const postfix = withYear && freq !== 'year' ? ` ${String(dt.year)}` : ''
  if (freq === 'month') return `${dt.toFormat('LLL')}${postfix}`
  if (freq === 'quarter') return `Q${dt.quarter}${postfix}`
  if (freq === 'year') return String(dt.year)
  return `${dt.toFormat('LLL dd')}${postfix}`
}

/**
 * Builds the axis's per-period render data. `dates` is oldest-first (see
 * `analyticsStore.getDatesRange`), so a period marks a year boundary when its
 * year differs from the previous entry's, or it's the very first period.
 */
export const buildAxisPeriods = (dates: string[], freq: PeriodFreq): AxisPeriod[] => {
  let previousYear: number | undefined
  const lastDate = dates.at(-1)
  return dates.map((date, index) => {
    const year = stringToDateTime(date)?.year
    const isYearStart = previousYear === undefined || year !== previousYear
    previousYear = year
    return {
      date,
      label: index === dates.length - 1 ? stringToLocalDateString(lastDate!) ?? '' : axisLabel(date, freq),
      yearFull: year !== undefined ? String(year) : '',
      yearShort: year !== undefined ? `'${String(year).slice(-2)}` : '',
      isYearStart,
    }
  })
}

export const getSelectedLabel = (date: string, freq: PeriodFreq, isLast: boolean) => {
  return `${isLast ? 'End date' : axisLabel(date, freq, true)} · ${stringToLocalDateString(date)}`
}
